import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Clock, User, Share2, Linkedin, Twitter, Facebook } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useBlogContactPopup } from "@/hooks/useBlogContactPopup";
import DemoScheduler from "@/components/DemoScheduler";
import SEO from "@/components/SEO";

const BlogPostImplementarIA = () => {
    const { isOpen, setIsOpen } = useBlogContactPopup(3000);

    const shareUrl = "https://aroan.com.br/blog/implementar-ia-service-desk";
    const shareTitle = "Como Implementar IA no Service Desk: Guia Completo para 2025";
    const shareDescription = "Descubra como implementar IA no seu service desk com nosso guia completo. Estratégias práticas, benefícios e melhores práticas para transformar seu atendimento.";

    const handleLinkedInShare = () => {
        const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        window.open(linkedInUrl, '_blank', 'width=600,height=400');
    };

    const handleTwitterShare = () => {
        const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
        window.open(twitterUrl, '_blank', 'width=600,height=400');
    };

    const handleFacebookShare = () => {
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        window.open(facebookUrl, '_blank', 'width=600,height=400');
    };

    return (
        <div className="min-h-screen bg-background">
            <SEO
                title="Como Implementar IA em Service Desk Sem Resistência da Equipe"
                description="Guia completo para implementar IA no Service Desk superando a resistência organizacional. Framework de 5 fases baseado em pesquisas da McKinsey e Harvard Business Review para transformar sua equipe em defensora da mudança."
                canonical="https://aroan.com.br/blog/implementar-ia-service-desk"
                ogImage="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&auto=format&fit=crop"
                author="Equipe Aroan"
                publishedTime="2024-12-17T00:00:00-03:00"
                keywords="implementação de IA, service desk, gestão de mudança, resistência organizacional, adopção de IA, ITSM, transformação digital"
            />
            <Header />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 bg-gradient-hero overflow-hidden">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <Badge className="mb-6 bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                            Gestão de Mudança
                        </Badge>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-tight animate-fade-in">
                            Como Implementar IA em <span className="text-primary">Service Desk</span> Sem Resistência da Equipe
                        </h1>

                        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground animate-fade-in-delay-1">
                            <div className="flex items-center gap-2">
                                <User className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">Equipe Aroan</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">17 Dez, 2024</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">15 min de leitura</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Image */}
            <section className="pb-12">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl animate-scale-in">
                        <img
                            src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&auto=format&fit=crop"
                            alt="Implementação de IA em Service Desk"
                            className="w-full h-[400px] sm:h-[500px] object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-12 bg-background">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-7xl mx-auto">
                        {/* Mobile: Single column, Desktop: Two columns */}
                        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
                            {/* Left Column */}
                            <div className="space-y-12">
                                {/* Introduction */}
                                <div className="prose prose-lg dark:prose-invert max-w-none">
                                    <p className="lead text-xl sm:text-2xl text-muted-foreground leading-relaxed font-medium">
                                        A implementação de Inteligência Artificial (IA) no Service Desk promete uma revolução em eficiência e qualidade de serviço. No entanto, a maioria das iniciativas de IA não falha por causa da tecnologia, mas sim devido a barreiras organizacionais.
                                    </p>
                                    <p>
                                        Uma pesquisa com executivos C-level revela que <strong>45% consideram o ROI da adoção de IA abaixo do esperado</strong>, e as barreiras mais significativas são pessoas, processos e políticas internas. A resistência da equipe, alimentada pela incerteza e pelo medo, é o principal obstáculo.
                                    </p>
                                    <p>
                                        Este artigo apresenta um framework de 5 fases com estratégias práticas para transformar a equipe do Service Desk de resistentes a defensores da mudança, garantindo uma transição suave e a captura de valor real da IA.
                                    </p>
                                </div>

                                {/* Section 1 - Understanding Resistance */}
                                <div className="prose prose-lg dark:prose-invert max-w-none">
                                    <h2 className="text-3xl font-bold mb-6 text-foreground">Entendendo a Raiz da Resistência</h2>
                                    <p>
                                        Para superar a resistência, é preciso primeiro compreendê-la. A resistência à IA raramente é irracional. Ela se origina de preocupações legítimas que se enquadram em três categorias principais, conforme identificado pela pesquisa da Harvard Business Review.
                                    </p>

                                    <div className="space-y-6">
                                        <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
                                            <h3 className="text-xl font-bold mb-3 text-red-800">1. Pessoas: Incerteza e Medo</h3>
                                            <p className="mb-4 text-red-700">
                                                A equipe não entende o que a IA fará, teme ser substituída ou se preocupa em parecer incompetente ao usar as novas ferramentas. <strong>(61% dos funcionários tiveram menos de 5 horas de treinamento em IA)</strong>.
                                            </p>
                                            <p className="text-sm italic text-red-600 mb-0">
                                                <strong>Exemplo no Service Desk:</strong> Um analista experiente que teme que um chatbot de IA o torne obsoleto ou que se sinta constrangido ao precisar de ajuda da IA para resolver um problema.
                                            </p>
                                        </div>

                                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
                                            <h3 className="text-xl font-bold mb-3 text-yellow-800">2. Processos: Inércia e Disrupção</h3>
                                            <p className="mb-4 text-yellow-700">
                                                A IA é vista como uma camada extra sobre processos existentes, em vez de uma oportunidade para redesenhar o trabalho. A mudança de workflows estabelecidos é desconfortável.
                                            </p>
                                            <p className="text-sm italic text-yellow-600 mb-0">
                                                <strong>Exemplo no Service Desk:</strong> Implementar uma IA que apenas sugere artigos da base de conhecimento no final do processo de um ticket, em vez de usá-la para triagem, roteamento e resolução automática desde o início.
                                            </p>
                                        </div>

                                        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
                                            <h3 className="text-xl font-bold mb-3 text-blue-800">3. Política: Perda de Poder e Status</h3>
                                            <p className="mb-4 text-blue-700">
                                                A IA ameaça hierarquias baseadas em conhecimento especializado e no tamanho da equipe. Gestores podem resistir se a eficiência da IA significar uma equipe menor e, consequentemente, menos influência.
                                            </p>
                                            <p className="text-sm italic text-blue-600 mb-0">
                                                <strong>Exemplo no Service Desk:</strong> Um especialista sênior que "acumula" conhecimento e resolve os tickets mais complexos pode ver a IA como uma ameaça direta ao seu status de "herói" da equipe.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="bg-card p-6 rounded-xl border border-border mt-8">
                                        <p className="text-lg font-medium text-center mb-0">
                                            Reconhecer essas barreiras não como insubordinação, mas como reações humanas previsíveis, é o primeiro passo para uma estratégia de mudança bem-sucedida.
                                        </p>
                                    </div>
                                </div>

                                {/* Conclusion */}
                                <div className="prose prose-lg dark:prose-invert max-w-none">
                                    <h2 className="text-3xl font-bold mb-6 text-foreground">Conclusão</h2>
                                    <p>
                                        A implementação de IA no Service Desk é, fundamentalmente, um exercício de gestão da mudança centrado em pessoas. O sucesso não depende da sofisticação dos algoritmos, mas da habilidade da liderança em criar uma visão positiva, construir confiança, envolver a equipe no processo e demonstrar que a IA é uma ferramenta de capacitação, não de substituição.
                                    </p>
                                    <p>
                                        Ao seguir este framework de 5 fases, as organizações podem transformar a ansiedade e a resistência em entusiasmo e defesa, garantindo que o investimento em IA se traduza em uma equipe mais eficiente, engajada e preparada para o futuro.
                                    </p>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-12">
                                {/* Framework Section */}
                                <div className="prose prose-lg dark:prose-invert max-w-none">
                                    <h2 className="text-3xl font-bold mb-6 text-foreground">Framework de 5 Fases para uma Adoção de Sucesso</h2>
                                    <p>
                                        Transformar a equipe em defensora da IA requer uma abordagem estruturada e empática. Este framework de 5 fases combina as melhores práticas de gestão de mudança da McKinsey e da Harvard Business Review.
                                    </p>

                                    <div className="space-y-8">
                                        <div className="bg-card p-6 rounded-xl border border-border">
                                            <h3 className="text-2xl font-bold mb-4 text-primary">Fase 1: Estabelecer a Visão e Construir a Confiança (O "Porquê")</h3>
                                            <p className="mb-4">
                                                Antes de qualquer implementação técnica, é crucial construir a base psicológica para a mudança.
                                            </p>
                                            <ul className="space-y-2">
                                                <li><strong>Crie uma "Estrela Guia" Clara:</strong> Defina e comunique uma visão inspiradora focada em resultados, não na ferramenta.</li>
                                                <li><strong>Enfatize a Augmentação, Não a Substituição:</strong> A narrativa deve ser sobre criar "superpoderes" para a equipe.</li>
                                                <li><strong>Estabeleça Governança Transparente:</strong> Adote um framework simples e claro, como o PURE.</li>
                                            </ul>
                                        </div>

                                        <div className="bg-card p-6 rounded-xl border border-border">
                                            <h3 className="text-2xl font-bold mb-4 text-primary">Fase 2: Envolver a Equipe e Cocriar a Solução (O "Quem")</h3>
                                            <p className="mb-4">
                                                A resistência diminui drasticamente quando a equipe se torna parte da solução, não o alvo dela.
                                            </p>
                                            <ul className="space-y-2">
                                                <li><strong>Identifique e Empodere "Campeões da IA":</strong> Encontre membros naturalmente curiosos sobre a IA.</li>
                                                <li><strong>Comece Pelas Dores Reais da Equipe:</strong> Pergunte qual trabalho repetitivo eles gostariam que um robô fizesse.</li>
                                                <li><strong>Adote a Cocriação:</strong> Use uma abordagem "dois-em-um" (two-in-a-box).</li>
                                            </ul>
                                        </div>

                                        <div className="bg-card p-6 rounded-xl border border-border">
                                            <h3 className="text-2xl font-bold mb-4 text-primary">Fase 3: Implementar, Treinar e Integrar (O "Como")</h3>
                                            <p className="mb-4">
                                                A forma como a IA é introduzida no trabalho diário é determinante para sua adoção.
                                            </p>
                                            <ul className="space-y-2">
                                                <li><strong>Redesenhe Workflows, Não Adicione Camadas:</strong> A IA deve estar no centro do novo fluxo de trabalho.</li>
                                                <li><strong>Invista Massivamente em Treinamento Formal:</strong> 48% dos funcionários usariam mais IA se recebessem treinamento.</li>
                                                <li><strong>Integre a IA ao Fluxo de Trabalho Diário:</strong> Deve estar na tela principal do Service Desk.</li>
                                            </ul>
                                        </div>

                                        <div className="bg-card p-6 rounded-xl border border-border">
                                            <h3 className="text-2xl font-bold mb-4 text-primary">Fase 4: Demonstrar Valor e Compartilhar os Ganhos (O "O que eu ganho?")</h3>
                                            <p className="mb-4">
                                                Para que a equipe abrace a mudança, ela precisa ver e sentir os benefícios.
                                            </p>
                                            <ul className="space-y-2">
                                                <li><strong>Comunique as Vitórias Iniciais:</strong> Celebre e comunique amplamente os sucessos.</li>
                                                <li><strong>Crie um Contrato de Confiança:</strong> Comprometa-se a reinvestir ganhos em bônus e treinamento.</li>
                                                <li><strong>Mude o Foco do Trabalho:</strong> Use o tempo liberado para projetos de maior valor.</li>
                                            </ul>
                                        </div>

                                        <div className="bg-card p-6 rounded-xl border border-border">
                                            <h3 className="text-2xl font-bold mb-4 text-primary">Fase 5: Escalar, Iterar e Normalizar (O "E agora?")</h3>
                                            <p className="mb-4">
                                                Adoção é um processo contínuo, não um evento único.
                                            </p>
                                            <ul className="space-y-2">
                                                <li><strong>Liderança pelo Exemplo:</strong> A liderança deve usar e referenciar a IA constantemente.</li>
                                                <li><strong>Crie um Ciclo de Feedback Contínuo:</strong> Estabeleça canais fáceis para feedback da equipe.</li>
                                                <li><strong>Torne a Proficiência em IA um Símbolo de Status:</strong> Reconheça e promova quem demonstra maestria.</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CTA Section */}
                        <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-8 rounded-2xl text-center mt-16">
                            <h3 className="text-2xl font-bold mb-4">Pronto para Implementar IA no Seu Service Desk?</h3>
                            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                                Descubra como a Aroan pode ajudar sua equipe a abraçar a IA e transformar a resistência em entusiasmo. Agende uma demonstração personalizada.
                            </p>
                            <Button size="lg" className="group" onClick={() => setIsOpen(true)}>
                                Agendar Demonstração
                                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Button>
                        </div>

                        {/* References */}
                        <div className="prose prose-lg dark:prose-invert max-w-none mt-12">
                            <h3 className="text-xl font-bold mb-4">Referências</h3>
                            <div className="text-sm text-muted-foreground space-y-2">
                                <p>
                                    McKinsey & Company. (2025). <em>Reconfiguring work: Change management in the age of gen AI</em>.
                                    <a href="https://www.mckinsey.com/capabilities/quantumblack/our-insights/reconfiguring-work-change-management-in-the-age-of-gen-ai"
                                        className="text-primary hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                                        Link para o estudo
                                    </a>
                                </p>
                                <p>
                                    Harvard Business Review. (2025). <em>Overcoming the Organizational Barriers to AI Adoption</em>.
                                    <a href="https://hbr.org/2025/11/overcoming-the-organizational-barriers-to-ai-adoption"
                                        className="text-primary hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                                        Link para o artigo
                                    </a>
                                </p>
                            </div>
                        </div>

                        {/* Share Section */}
                        <div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
                            <div>
                                <h4 className="font-semibold mb-2">Compartilhe este artigo</h4>
                                <div className="flex gap-3">
                                    <Button size="sm" variant="outline" className="group" onClick={handleLinkedInShare}>
                                        <Linkedin className="w-4 h-4 mr-2 group-hover:text-primary transition-colors" />
                                        LinkedIn
                                    </Button>
                                    <Button size="sm" variant="outline" className="group" onClick={handleTwitterShare}>
                                        <Twitter className="w-4 h-4 mr-2 group-hover:text-primary transition-colors" />
                                        Twitter
                                    </Button>
                                    <Button size="sm" variant="outline" className="group" onClick={handleFacebookShare}>
                                        <Facebook className="w-4 h-4 mr-2 group-hover:text-primary transition-colors" />
                                        Facebook
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
            <DemoScheduler open={isOpen} onOpenChange={setIsOpen} />
        </div>
    );
};

export default BlogPostImplementarIA;
